"use client"

import classNames from "classnames"
import React, { CSSProperties } from "react"
import { Tooltip, TooltipProps } from "antd"

import useIsOverflow from "./useIsOverflow"

import "./index.scss"

// 定义组件的 Props 接口
// Omit 用于排除 TooltipProps 中我们想要覆盖或不需要的属性
// 同时排除 className 和 style，因为我们会分别定义用于容器和 Tooltip 的样式属性
export interface EllipsisTooltipProps extends Omit<TooltipProps, "className" | "style"> {
  // 容器 div 的样式
  style?: CSSProperties
  className?: string
  // Tooltip 组件的样式
  tooltipStyle?: CSSProperties
  tooltipClassName?: string
  rows?: number // 行数，默认为 1
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  children,
  style,
  className,
  tooltipStyle,
  tooltipClassName,
  open, // 允许外部覆盖 open，但默认由内部控制
  rows = 1,
  ...restProps
}) => {
  // 1. 调用 Hook，获取溢出状态和 ref 引用
  // 只需要支持 div，因为不再内部渲染 Link
  const [isOverflowing, textRef] = useIsOverflow<HTMLDivElement>()

  // 计算最终的 open 状态
  // 如果外部传入了 open，则尊重外部控制；否则根据溢出状态决定
  const tooltipOpen = open !== undefined ? open : isOverflowing ? undefined : false

  const containerClass = classNames("ui-ellipsis-tooltip ui-ellipsis-tooltip--multiline", className)

  const containerStyle: CSSProperties = { WebkitLineClamp: rows, ...style }

  return (
    <Tooltip
      open={tooltipOpen}
      mouseEnterDelay={0.5} // 默认延迟 0.5s，防止闪烁
      className={tooltipClassName}
      style={tooltipStyle}
      {...restProps}
    >
      <div ref={textRef} className={containerClass} style={containerStyle}>
        {children}
      </div>
    </Tooltip>
  )
}

export default EllipsisTooltip
export { useIsOverflow }
